import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { Permission } from '../../models/permission.model';

/** @internal — not exported from barrel */
interface PermissionGroup {
  resource: string;
  label: string;
  icon: string;
  permissions: Permission[];
}

/** @internal — not exported from barrel */
const RESOURCE_META: Record<string, { label: string; icon: string }> = {
  tenants:          { label: 'Tenants',        icon: 'pi pi-building' },
  users:            { label: 'Users',          icon: 'pi pi-users' },
  roles:            { label: 'Roles',          icon: 'pi pi-id-card' },
  permissions:      { label: 'Permissions',    icon: 'pi pi-lock' },
  'api-keys':       { label: 'API Keys',       icon: 'pi pi-key' },
  groups:           { label: 'Groups',         icon: 'pi pi-sitemap' },
  projects:         { label: 'Projects',       icon: 'pi pi-folder' },
  stages:           { label: 'Stages',         icon: 'pi pi-flag' },
  origins:          { label: 'Origins',        icon: 'pi pi-compass' },
  'origin-groups':  { label: 'Origin Groups',  icon: 'pi pi-compass' },
  objections:       { label: 'Objections',     icon: 'pi pi-comment' },
  webhooks:         { label: 'Webhooks',       icon: 'pi pi-link' },
  'webhook-logs':   { label: 'Webhook Logs',   icon: 'pi pi-list' },
  channels:         { label: 'Channels',       icon: 'pi pi-comments' },
  prospects:        { label: 'Prospects',      icon: 'pi pi-address-book' },
  'system-configs': { label: 'System Configs', icon: 'pi pi-cog' },
};

/** @internal — not exported from barrel */
function groupPermissions(perms: Permission[]): PermissionGroup[] {
  const byResource = new Map<string, Permission[]>();
  for (const perm of perms) {
    const resource = perm.name.split(':')[0];
    const bucket = byResource.get(resource);
    if (bucket) bucket.push(perm);
    else byResource.set(resource, [perm]);
  }

  return Array.from(byResource.entries())
    .map(([resource, permissions]) => {
      const meta = RESOURCE_META[resource];
      return {
        resource,
        label: meta?.label ?? capitalize(resource),
        icon: meta?.icon ?? 'pi pi-folder',
        permissions: [...permissions].sort((a, b) =>
          (a.description ?? a.name).localeCompare(b.description ?? b.name),
        ),
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** @internal */
function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

@Component({
  selector: 'niko-permission-group-grid',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, ToggleSwitchModule],
  templateUrl: './permission-group-grid.component.html',
  styleUrl: './permission-group-grid.component.scss',
})
export class PermissionGroupGridComponent {
  /** Full list of permissions to display, grouped by resource. */
  readonly permissions = input.required<Permission[]>();

  /** Currently selected permission IDs. Parent-owned, immutable replace. */
  readonly selectedIds = input.required<Set<string>>();

  /**
   * Permission IDs the user already has THROUGH ITS ROLE. These render as
   * ON + locked with an "inherited" badge, so they cannot be re-assigned
   * directly (they are already effective via the role).
   */
  readonly roleIds = input<Set<string>>(new Set());

  /** Badge text shown on role-inherited permissions. Parent passes the i18n string. */
  readonly roleBadgeLabel = input('Inherited from role');

  /** When true, all toggles are disabled and no events are emitted. */
  readonly disabled = input(false);

  /** Emitted when a single permission toggle changes. */
  readonly permissionToggle = output<{ id: string; checked: boolean }>();

  /** Emitted when a group-level "select all" toggle changes (includes expanded permission IDs). */
  readonly toggleGroup = output<{ permissionIds: string[]; checked: boolean }>();

  /** Derived groups from permissions input. */
  protected readonly groups = computed(() =>
    groupPermissions(this.permissions()),
  );

  /** True when the permission comes from the role (locked, shows badge). */
  protected isInherited(id: string): boolean {
    return this.roleIds().has(id);
  }

  /** Effective state: ON if inherited from role OR directly selected. */
  protected isChecked(id: string): boolean {
    return this.roleIds().has(id) || this.selectedIds().has(id);
  }

  protected isSelected(id: string): boolean {
    return this.selectedIds().has(id);
  }

  /** Group "select all" is ON when every perm is effective (inherited or selected). */
  protected groupAllChecked(group: PermissionGroup): boolean {
    return group.permissions.every((p) => this.isChecked(p.id));
  }

  protected onToggle(id: string, checked: boolean): void {
    if (this.disabled() || this.isInherited(id)) return;
    this.permissionToggle.emit({ id, checked });
  }

  protected onToggleGroup(group: PermissionGroup, checked: boolean): void {
    if (this.disabled()) return;
    // Only emit for non-inherited perms; role-granted ones stay locked ON.
    this.toggleGroup.emit({
      permissionIds: group.permissions
        .filter((p) => !this.isInherited(p.id))
        .map((p) => p.id),
      checked,
    });
  }
}
